import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  actOnDeviation,
  type DeviationAction,
  type DeviationView,
  getDeviation,
  getMyDeviations,
  remindDeviation,
} from '../../../api/safetyRounds';
import { useChecklistLanguage } from './usePlanQueries';

export const deviationKeys = {
  all: ['safety-rounds', 'deviations'] as const,
  one: (id: number, language: string) => ['safety-rounds', 'deviations', id, language] as const,
  mine: (language: string) => ['safety-rounds', 'deviations', 'mine', language] as const,
};

export const useDeviationQuery = (id: number) => {
  const language = useChecklistLanguage();
  return useQuery({ queryKey: deviationKeys.one(id, language), queryFn: () => getDeviation(id, language) });
};

export const useMyDeviationsQuery = (enabled = true) => {
  const language = useChecklistLanguage();
  return useQuery({ queryKey: deviationKeys.mine(language), queryFn: () => getMyDeviations(language), enabled });
};

/** Every change answers with the case, so the page updates without fetching it again */
const useDeviationMutation = <T>(id: number, mutationFn: (input: T, language: string) => Promise<DeviationView>) => {
  const queryClient = useQueryClient();
  const language = useChecklistLanguage();
  return useMutation({
    mutationFn: (input: T) => mutationFn(input, language),
    onSuccess: (view) => {
      queryClient.setQueryData(deviationKeys.one(id, language), view);
      queryClient.invalidateQueries({ queryKey: deviationKeys.all, exact: false, refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: deviationKeys.mine(language) });
      queryClient.invalidateQueries({ queryKey: ['safety-rounds', 'protocols'] });
      queryClient.invalidateQueries({ queryKey: ['safety-rounds', 'plans'] });
    },
  });
};

export const useDeviationActionMutation = (id: number) =>
  useDeviationMutation(
    id,
    (
      { action, input }: { action: Exclude<DeviationAction, 'remind'>; input: { comment?: string; dueDate?: string } },
      language,
    ) => actOnDeviation(id, action, input, language),
  );
export const useRemindDeviationMutation = (id: number) =>
  useDeviationMutation(id, (_: void, language) => remindDeviation(id, language));
